import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const campaignsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Local mirror of the WABA's Meta-approved templates. Approval status is owned by Meta and
    // only ever refreshed by syncWhatsAppTemplates below.
    listWhatsAppTemplates: build.query({
      query: (filter) => `/whatsapp-templates${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: ['WaTemplate'],
    }),
    getWhatsAppTemplate: build.query({
      query: (id) => `/whatsapp-templates/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: ['WaTemplate'],
    }),
    // Tells the UI whether WabaId + a management token are present, so the Templates tab can show
    // a setup hint instead of a Sync button that is guaranteed to fail.
    getWhatsAppTemplateConfig: build.query({
      query: () => '/whatsapp-templates/config',
      transformResponse: (resp) => resp.data,
      providesTags: ['WaTemplate'],
    }),
    // Pulls approval status/copy from Meta. Sends no messages, so it stays available even while
    // outbound WhatsApp is paused for maintenance.
    syncWhatsAppTemplates: build.mutation({
      query: () => ({ url: '/whatsapp-templates/sync', method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['WaTemplate'],
    }),
    // The one definition of which CRM fields a variable may pull from — shared by the binding
    // dropdown and the server-side validator, so the two can't disagree.
    getCrmTokens: build.query({
      query: (kind) => `/whatsapp-templates/tokens${kind ? `?kind=${kind}` : ''}`,
      transformResponse: (resp) => resp.data,
    }),
    createWhatsAppTemplate: build.mutation({
      query: (body) => ({ url: '/whatsapp-templates', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['WaTemplate'],
    }),
    // Copy + variable structure. The server rejects this with 403 once Meta has approved the
    // template, since it's immutable there at that point.
    updateWhatsAppTemplate: build.mutation({
      query: ({ id, ...body }) => ({ url: `/whatsapp-templates/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['WaTemplate'],
    }),
    // CRM field bindings only — allowed at any approval status.
    updateTemplateBindings: build.mutation({
      query: ({ id, ...body }) => ({ url: `/whatsapp-templates/${id}/bindings`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['WaTemplate'],
    }),
    submitWhatsAppTemplate: build.mutation({
      query: (id) => ({ url: `/whatsapp-templates/${id}/submit`, method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['WaTemplate'],
    }),
    deleteWhatsAppTemplate: build.mutation({
      query: (id) => ({ url: `/whatsapp-templates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['WaTemplate'],
    }),
    // One-off send to an arbitrary recipient — the "Send Message" modal's WhatsApp path. Distinct
    // from an Automation: variables are typed by staff, not resolved from a CRM record.
    sendWhatsAppTemplate: build.mutation({
      query: ({ id, ...body }) => ({ url: `/whatsapp-templates/${id}/send`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Message'],
    }),

    // ── Audiences ─────────────────────────────────────────────────────────
    listAudiences: build.query({
      query: () => '/audiences',
      transformResponse: (resp) => resp.data,
      providesTags: ['Audience'],
    }),
    createAudience: build.mutation({
      query: (body) => ({ url: '/audiences', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Audience'],
    }),
    updateAudience: build.mutation({
      query: ({ id, ...body }) => ({ url: `/audiences/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Audience'],
    }),
    deleteAudience: build.mutation({
      query: (id) => ({ url: `/audiences/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Audience'],
    }),
    // Previews an UNSAVED rule, so the count can be checked before committing to it. A mutation
    // rather than a query because it POSTs the whole definition and shouldn't be cached.
    previewAudienceDraft: build.mutation({
      query: (body) => ({ url: '/audiences/preview?sampleSize=25', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
    }),
    previewAudience: build.query({
      query: (id) => `/audiences/${id}/preview?sampleSize=25`,
      transformResponse: (resp) => resp.data,
      providesTags: ['Audience'],
    }),

    // ── Automations ───────────────────────────────────────────────────────
    listAutomations: build.query({
      query: () => '/automations',
      transformResponse: (resp) => resp.data,
      providesTags: ['Automation'],
    }),
    // What's going out next, with a live recipient estimate per automation.
    listUpcomingSends: build.query({
      query: (limit = 10) => `/automations/upcoming?limit=${limit}`,
      transformResponse: (resp) => resp.data,
      providesTags: ['Automation'],
    }),
    createAutomation: build.mutation({
      query: (body) => ({ url: '/automations', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Automation'],
    }),
    // "Send Message"'s audience path: creates a one-off automation and runs it immediately. Shows
    // up in the Automations tab afterward like any other campaign, for audit/history.
    quickSendAutomation: build.mutation({
      query: (body) => ({ url: '/automations/quick-send', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Automation', 'AutomationRun', 'Message'],
    }),
    updateAutomation: build.mutation({
      query: ({ id, ...body }) => ({ url: `/automations/${id}`, method: 'PUT', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Automation'],
    }),
    deleteAutomation: build.mutation({
      query: (id) => ({ url: `/automations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Automation'],
    }),
    // 409s with the reason when the template isn't approved or its variables aren't bound.
    toggleAutomation: build.mutation({
      query: (id) => ({ url: `/automations/${id}/toggle`, method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Automation'],
    }),
    runAutomationNow: build.mutation({
      query: (id) => ({ url: `/automations/${id}/run-now`, method: 'POST' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: ['Automation', 'AutomationRun', 'Message'],
    }),
    // What Run now would do, without sending anything — how many are already claimed for this
    // occurrence (and would be skipped) vs. how many new people would actually be messaged. Backs
    // the confirmation shown before a real run.
    previewAutomationRun: build.query({
      query: (id) => `/automations/${id}/run-preview`,
      transformResponse: (resp) => resp.data,
    }),
    // Stops a currently-sending run after its in-flight recipient. Whoever it already claimed
    // keeps their claim — a later run won't re-message them.
    cancelAutomationRun: build.mutation({
      query: (runId) => ({ url: `/automations/runs/${runId}/cancel`, method: 'POST' }),
      invalidatesTags: ['Automation', 'AutomationRun'],
    }),
    listAutomationRuns: build.query({
      query: ({ id, ...paging }) => `/automations/${id}/runs?page=${paging.page ?? 1}&pageSize=${paging.pageSize ?? 10}`,
      transformResponse: (resp) => resp.data,
      providesTags: ['AutomationRun'],
    }),
    listAutomationSends: build.query({
      query: ({ runId, ...paging }) => `/automations/runs/${runId}/sends?page=${paging.page ?? 1}&pageSize=${paging.pageSize ?? 50}`,
      transformResponse: (resp) => resp.data,
      providesTags: ['AutomationRun'],
    }),
  }),
});

export const {
  useListWhatsAppTemplatesQuery,
  useGetWhatsAppTemplateQuery,
  useGetWhatsAppTemplateConfigQuery,
  useSyncWhatsAppTemplatesMutation,
  useGetCrmTokensQuery,
  useCreateWhatsAppTemplateMutation,
  useUpdateWhatsAppTemplateMutation,
  useUpdateTemplateBindingsMutation,
  useSubmitWhatsAppTemplateMutation,
  useDeleteWhatsAppTemplateMutation,
  useSendWhatsAppTemplateMutation,
  useListAudiencesQuery,
  useCreateAudienceMutation,
  useUpdateAudienceMutation,
  useDeleteAudienceMutation,
  usePreviewAudienceDraftMutation,
  usePreviewAudienceQuery,
  useQuickSendAutomationMutation,
  useListAutomationsQuery,
  useListUpcomingSendsQuery,
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
  useDeleteAutomationMutation,
  useToggleAutomationMutation,
  useRunAutomationNowMutation,
  usePreviewAutomationRunQuery,
  useCancelAutomationRunMutation,
  useListAutomationRunsQuery,
  useListAutomationSendsQuery,
} = campaignsApi;
